<script>
  import { onMount } from 'svelte';
  import { getApiUrl } from '../api';
  import { authFetch } from '../authService';

  let message = '';
  let isSending = false;
  let error = '';
  let success = '';

  // History State
  let history = [];
  let page = 1;
  const pageSize = 10;
  let totalPages = 1;
  let isLoadingHistory = true;

  onMount(() => {
    fetchHistory();
  });

  async function handleBroadcast() {
    if (!message.trim()) return;
    
    if(!confirm("Are you sure you want to broadcast this message to ALL online users?")) return;

    isSending = true;
    error = '';
    success = '';

    try {
      const res = await authFetch(getApiUrl('/admin/broadcast'), {
        method: 'POST',
        body: JSON.stringify({ message })
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.error || data.details || "Failed to send.");

      success = "Message sent successfully!";
      message = '';
      fetchHistory(); // Refresh list

    } catch (e) {
      error = e.message;
    } finally {
      isSending = false;
    }
  }

  async function fetchHistory() {
    isLoadingHistory = true;
    try {
      const res = await authFetch(getApiUrl(`/admin/broadcast/history?page=${page}&pageSize=${pageSize}`));
      const data = await res.json();
      history = data.data;
      totalPages = data.totalPages;
    } catch (e) {
      console.error(e);
    } finally {
      isLoadingHistory = false;
    }
  }

  function nextPage() { if(page < totalPages) { page++; fetchHistory(); } }
  function prevPage() { if(page > 1) { page--; fetchHistory(); } }
</script>

<div class="admin-container">
  <h2>Broadcast Message</h2>
  
  <div class="card form-section">
    <label for="msg">Message Content</label>
    <textarea id="msg" bind:value={message} rows="3" placeholder="Enter important announcement..."></textarea>
    
    <div class="actions">
      <button class="primary" on:click={handleBroadcast} disabled={isSending || !message.trim()}>
        {isSending ? 'Sending...' : 'Broadcast Now'}
      </button>
    </div>

    {#if success} <p class="success">{success}</p> {/if}
    {#if error} <p class="error">{error}</p> {/if}
  </div>

  <div class="card history-section">
    <h3>Broadcast History</h3>
    {#if isLoadingHistory}
        <p>Loading...</p>
    {:else if history.length === 0}
        <p>No broadcast history found.</p>
    {:else}
        <table>
            <thead>
                <tr>
                    <th>Date</th>
                    <th>Message</th>
                    <th>Status</th>
                    <th>Sender ID</th>
                </tr>
            </thead>
            <tbody>
                {#each history as log}
                    <tr>
                        <td>{new Date(log.createdAt).toLocaleString()}</td>
                        <td>{log.message}</td>
                        <td>
                            <span class="badge {log.status === 'Success' ? 'success-bg' : 'error-bg'}">
                                {log.status}
                            </span>
                            {#if log.status === 'Failed'}
                                <br/><small class="err-text">{log.errorMessage}</small>
                            {/if}
                        </td>
                        <td class="small-id">{log.senderId}</td>
                    </tr>
                {/each}
            </tbody>
        </table>
        
        <div class="pagination">
            <button disabled={page <= 1} on:click={prevPage}>Prev</button>
            <span>Page {page} of {totalPages}</span>
            <button disabled={page >= totalPages} on:click={nextPage}>Next</button>
        </div>
    {/if}
  </div>
</div>

<style>
  .admin-container { max-width: 900px; margin: 2rem auto; padding: 0 1rem; }
  .card { background: #fff; padding: 1.5rem; border-radius: 8px; box-shadow: 0 2px 4px rgba(0,0,0,0.05); margin-bottom: 2rem; }
  
  textarea { width: 100%; padding: 0.8rem; border: 1px solid #ccc; border-radius: 4px; box-sizing: border-box; font-size: 1rem; margin-top: 0.5rem; }
  label { font-weight: bold; color: #333; }
  
  .actions { margin-top: 1rem; text-align: right; }
  button { padding: 0.6rem 1.2rem; border-radius: 4px; border: none; cursor: pointer; font-size: 1rem; }
  .primary { background-color: #dc3545; color: white; } /* Red for caution */
  .primary:disabled { background-color: #e6a8ad; }
  
  table { width: 100%; border-collapse: collapse; margin-top: 1rem; }
  th, td { padding: 10px; border-bottom: 1px solid #eee; text-align: left; }
  th { background: #f8f9fa; }
  
  .badge { padding: 3px 8px; border-radius: 12px; font-size: 0.85rem; font-weight: bold; }
  .success-bg { background: #e2f0d9; color: #28a745; }
  .error-bg { background: #f8d7da; color: #721c24; }
  .err-text { color: red; font-size: 0.8rem; }
  .small-id { font-family: monospace; font-size: 0.85rem; color: #666; }

  .pagination { display: flex; justify-content: center; gap: 1rem; margin-top: 1rem; align-items: center; }
  .pagination button { background: #f0f0f0; color: #333; }

  .success { color: #28a745; text-align: center; font-weight: bold; }
  .error { color: #dc3545; text-align: center; font-weight: bold; }
</style>
